import { needsVariantPick } from '@/utils/substance/variantGroups';
import { isGroupNode } from '@rules/grammar';
import { DECISIONS } from '@rules/tree';
import { requirementHolds } from '@rules/vocabulary';
import type {
  RuleSchema,
  SchemaFieldNode,
  SchemaGroupNode,
  Visibility,
  VisibilityPredicateId,
} from '@rules/grammar';
import type { RuleContext } from './ruleContext';

// Rule evaluation lives on BE (see RuleInterpreter.cs); FE only gates display.

export type VisibilityPredicate = (ctx: RuleContext) => boolean;

export const visibilityPredicates = {
  // Mirror of BE NamedPredicates.multipleMatchesVisible: only prompt for a variant pick
  // when nothing is selected yet, so an auto-selected multiple-match isn't "needs selection".
  multipleMatchesVisible: (ctx) => needsVariantPick(ctx.validationResult, ctx.cargo),
  // T75: tank container OR outer packaging code starting with "T75".
  t75HoldingTime: (ctx) => {
    if (ctx.isTankUnit) return ctx.ctuContainerType === 'T75';
    const code = ctx.cargo.outerPackaging?.code;
    return code === 'T75' || code?.startsWith('T75 ') === true;
  },
} satisfies Record<VisibilityPredicateId, VisibilityPredicate>;

function evalVisibility(v: Visibility, ctx: RuleContext): boolean {
  switch (v.kind) {
    case 'requires':
      return requirementHolds(ctx.answers, DECISIONS, v.requires, ctx.isMarinePollutant);
    case 'ctxFlag':
      return ctx[v.ctxFlag] === true;
    case 'named':
      return visibilityPredicates[v.named](ctx);
    case 'not':
      return !evalVisibility(v.not, ctx);
    case 'all':
      return v.all.every((sub) => evalVisibility(sub, ctx));
    case 'any':
      return v.any.some((sub) => evalVisibility(sub, ctx));
    case 'allBlank':
      // Rules-only op; not used in any current `visible:` clause. If a future
      // tree change uses it in visibility, add support here.
      return false;
  }
}

export function isVisible<S>(tree: RuleSchema<S>, path: string, ctx: RuleContext): boolean {
  const segments = path.split('.');
  let scope: Record<string, SchemaFieldNode<S> | SchemaGroupNode<S>> = tree;
  let current: SchemaFieldNode<S> | SchemaGroupNode<S> | undefined;
  for (const seg of segments) {
    current = scope[seg];
    if (!current) return false;
    if (!evalVisibility(current.visible, ctx)) return false;
    if (isGroupNode(current)) scope = current.children;
  }
  return current !== undefined;
}
